// Keep-alive de segundo plano por áudio inaudível.
//
// Problema: navegadores de celular (iOS Safari, Android Chrome) suspendem a aba
// quando ela vai para segundo plano (troca de app, tela bloqueada, outra aba).
// Ao suspender, o JavaScript congela e a conexão WebRTC da sala cai — o usuário
// precisa entrar de novo. Nem a reconexão automática do SDK roda, pois o motor
// JS está parado.
//
// Solução (mesma técnica usada por apps como o Jitsi): existe uma regra nos
// navegadores de que "abas tocando áudio não são suspensas/desaceleradas"
// (ver MDN Page Visibility API). Mantemos um oscilador de áudio INAUDÍVEL
// (ganho pertíssimo de zero) tocando enquanto o usuário está na sala. Ninguém
// ouve nada, não consome dados nem emite som audível — mas o SO passa a tratar
// a aba como "ativa" e para de congelá-la, reduzindo muito as quedas em segundo
// plano.
//
// Observações:
// - O AudioContext precisa de um gesto do usuário para iniciar em alguns
//   navegadores (regra de autoplay). Como entrar na sala já é um clique, na
//   maioria dos casos ele inicia direto; ainda assim, instalamos um fallback
//   que retoma o contexto no próximo toque/clique caso ele nasça "suspenso".
// - Reaquire (resume) ao voltar do segundo plano, pois o iOS costuma suspender
//   o AudioContext junto com a aba.
// - Degrada silenciosamente em navegadores sem Web Audio (nunca quebra a sala).

type KeepAliveHandle = {
  stop: () => void;
};

type WebkitWindow = Window & {
  webkitAudioContext?: typeof AudioContext;
};

// Ganho extremamente baixo: totalmente inaudível para humanos, mas suficiente
// para o navegador classificar a aba como "tocando áudio". Não usamos 0 puro
// porque alguns motores otimizam/ignoram um ganho zero.
const INAUDIBLE_GAIN = 0.0001;

export function startBackgroundKeepAlive(): KeepAliveHandle {
  if (typeof window === "undefined") {
    return { stop: () => undefined };
  }

  const AudioCtx =
    window.AudioContext || (window as WebkitWindow).webkitAudioContext;

  if (!AudioCtx) {
    return { stop: () => undefined };
  }

  let ctx: AudioContext | null = null;
  let oscillator: OscillatorNode | null = null;
  let gain: GainNode | null = null;
  let stopped = false;

  const ensureStarted = () => {
    if (stopped) {
      return;
    }
    try {
      if (!ctx) {
        ctx = new AudioCtx();
        gain = ctx.createGain();
        gain.gain.value = INAUDIBLE_GAIN;
        oscillator = ctx.createOscillator();
        // Frequência bem baixa (grave) só para manter o nó ativo; com o ganho
        // ínfimo é imperceptível de qualquer forma.
        oscillator.type = "sine";
        oscillator.frequency.value = 30;
        oscillator.connect(gain);
        gain.connect(ctx.destination);
        oscillator.start();
      }
      if (ctx.state === "suspended") {
        void ctx.resume().catch(() => undefined);
      }
    } catch {
      // Sem suporte / bloqueado: ignora silenciosamente.
    }
  };

  // Retoma o contexto quando o usuário volta para a aba (iOS suspende o
  // AudioContext junto com a página em segundo plano).
  const handleVisibility = () => {
    if (document.visibilityState === "visible") {
      ensureStarted();
    }
  };

  // Fallback: se o contexto nasceu "suspenso" (autoplay bloqueado), retoma no
  // próximo gesto do usuário. Reinstala listeners após voltar do segundo plano
  // (iOS suspende o AudioContext repetidamente).
  const installGestureFallback = () => {
    window.addEventListener("pointerdown", resumeOnGesture, { once: true });
    window.addEventListener("touchend", resumeOnGesture, { once: true });
    window.addEventListener("keydown", resumeOnGesture, { once: true });
  };

  const resumeOnGesture = () => {
    ensureStarted();
    installGestureFallback();
  };

  const handleVisibilityWithGesture = () => {
    handleVisibility();
    if (document.visibilityState === "visible") {
      installGestureFallback();
    }
  };

  ensureStarted();
  installGestureFallback();

  document.addEventListener("visibilitychange", handleVisibilityWithGesture);

  return {
    stop: () => {
      stopped = true;
      document.removeEventListener("visibilitychange", handleVisibilityWithGesture);
      window.removeEventListener("pointerdown", resumeOnGesture);
      window.removeEventListener("touchend", resumeOnGesture);
      window.removeEventListener("keydown", resumeOnGesture);
      try {
        oscillator?.stop();
      } catch {
        // já parado
      }
      try {
        oscillator?.disconnect();
        gain?.disconnect();
      } catch {
        // nós já desconectados
      }
      if (ctx) {
        void ctx.close().catch(() => undefined);
      }
      ctx = null;
      oscillator = null;
      gain = null;
    },
  };
}
